'use client';

import { useEffect, useRef } from 'react';

type VideoHlsProps = {
    hlsSrc: string | null;
    poster?: string | null;
    className?: string;
    autoPlay?: boolean;
};

export const VideoHls = ({ hlsSrc, poster, className, autoPlay = true }: VideoHlsProps) => {
    const ref = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
        (async () => {
            if (!hlsSrc) return;
            const video = ref.current;
            if (!video) return;

            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = hlsSrc;
                try {
                    if (autoPlay) await video.play();
                } catch {}
                return;
            }

            const Hls = (await import('hls.js')).default;
            if (Hls.isSupported()) {
                const hls = new Hls({ maxBufferLength: 15, maxMaxBufferLength: 30 });
                hls.loadSource(hlsSrc);
                hls.attachMedia(video);
                hls.on(Hls.Events.MANIFEST_PARSED, async () => {
                    try {
                        if (autoPlay) await video.play();
                    } catch {}
                });
                return () => hls.destroy();
            }
        })();
    }, [hlsSrc, autoPlay]);

    let autoplayVal = autoPlay;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        autoplayVal = false;
    }

    return (
        <video
            ref={ref}
            className={className}
            preload='auto'
            muted
            loop
            playsInline
            autoPlay={autoplayVal}
            poster={poster ?? undefined}
        />
    );
};
